import React, { useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Modal, Popconfirm, Space, Tag, Tooltip } from 'antd';
import { PlusOutlined, CopyOutlined, StopOutlined } from '@ant-design/icons';
import moment from 'moment';
// Api
import {
  CREATE_TALENT_INVITE_LINK_FOR_CAMPAIGN,
  CREATE_TALENT_INVITE_LINK_ALL_DEALS,
  DEACTIVATE_TALENT_INVITE_LINK,
} from 'api/campaignV2/invite/mutations';
import {
  GET_TALENT_INVITE_LINKS_FOR_CAMPAIGN,
  GET_TALENT_INVITE_LINKS,
} from 'api/campaignV2/invite/queries';
import getEnvVariables from 'api/env';
// UI
import Table from 'ui/Table';
import { successNotification, errorNotification } from 'ui/Notification';
// Styles
import styles from './TalentInviteLinksModal.module.scss';

type TalentInviteLink = {
  id: string;
  code: string;
  type: string;
  campaignId: string | null;
  inviteProcessingFee: number | null;
  createdAt: string;
};

type TalentInviteLinksModalProps = {
  show: boolean;
  handleClose: () => void;
  campaignId: string;
  agencyId: string;
  campaignProcessingFee?: number | null;
};

const DEFAULT_PROCESSING_FEE = 5;

const TalentInviteLinksModal = ({
  show,
  handleClose,
  campaignId,
  agencyId,
  campaignProcessingFee,
}: TalentInviteLinksModalProps): JSX.Element => {
  const effectiveFallbackFee = campaignProcessingFee ?? DEFAULT_PROCESSING_FEE;
  const {
    data: campaignLinksData,
    loading: campaignLinksLoading,
    refetch: refetchCampaignLinks,
  } = useQuery(GET_TALENT_INVITE_LINKS_FOR_CAMPAIGN, {
    variables: { input: { campaignId } },
    skip: !show,
    fetchPolicy: 'network-only',
  });

  const {
    data: allLinksData,
    loading: allLinksLoading,
    refetch: refetchAllLinks,
  } = useQuery(GET_TALENT_INVITE_LINKS, {
    variables: { input: { ownerId: agencyId } },
    skip: !show,
    fetchPolicy: 'network-only',
  });

  const [createForCampaign, { loading: creatingCampaignLink }] = useMutation(
    CREATE_TALENT_INVITE_LINK_FOR_CAMPAIGN
  );

  const [createAllDeals, { loading: creatingAllDealsLink }] = useMutation(
    CREATE_TALENT_INVITE_LINK_ALL_DEALS
  );

  const [deactivateLink] = useMutation(DEACTIVATE_TALENT_INVITE_LINK);

  const links: TalentInviteLink[] = useMemo(() => {
    const campaignLinks: TalentInviteLink[] =
      campaignLinksData?.getTalentInviteLinksForCampaign || [];

    const globalLinks: TalentInviteLink[] = (
      allLinksData?.getTalentInviteLinks || []
    ).filter((link: TalentInviteLink) => link.type === 'ALL_DEALS');

    const seen = new Set<string>();
    const merged: TalentInviteLink[] = [];

    [...campaignLinks, ...globalLinks].forEach((link) => {
      if (!seen.has(link.id)) {
        seen.add(link.id);
        merged.push(link);
      }
    });

    return merged;
  }, [campaignLinksData, allLinksData]);

  const buildInviteUrl = (code: string) =>
    `${getEnvVariables.REACT_APP_WEB_PORTAL_URL}/campaign/invite/${code}`;

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(buildInviteUrl(code));
      successNotification('Invite link copied to clipboard');
    } catch {
      errorNotification('Failed to copy link');
    }
  };

  const handleCreateCampaignLink = async () => {
    try {
      await createForCampaign({
        variables: { input: { campaignId } },
      });
      successNotification('Campaign invite link created');
      refetchCampaignLinks();
    } catch (error: any) {
      errorNotification(
        error?.message || 'Failed to create campaign invite link'
      );
    }
  };

  const handleCreateAllDealsLink = async () => {
    try {
      await createAllDeals({
        variables: { input: { ownerId: agencyId } },
      });
      successNotification('Global invite link created');
      refetchAllLinks();
    } catch (error: any) {
      errorNotification(
        error?.message || 'Failed to create global invite link'
      );
    }
  };

  const handleDeactivate = async (linkId: string) => {
    try {
      await deactivateLink({
        variables: { input: { linkId } },
      });
      successNotification('Invite link deactivated');
      refetchCampaignLinks();
      refetchAllLinks();
    } catch (error: any) {
      errorNotification(error?.message || 'Failed to deactivate invite link');
    }
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      align: 'center' as const,
      width: 150,
      render: (type: string) => (
        <Tag color={type === 'SPECIFIC_CAMPAIGN' ? 'blue' : 'green'}>
          {type === 'SPECIFIC_CAMPAIGN' ? 'Specific Campaign' : 'All Deals'}
        </Tag>
      ),
    },
    {
      title: 'Invite URL',
      dataIndex: 'code',
      align: 'center' as const,
      width: 300,
      render: (code: string) => {
        const url = buildInviteUrl(code);
        return (
          <Tooltip title={url}>
            <span className={styles.urlCell}>{url}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Processing Fee %',
      dataIndex: 'inviteProcessingFee',
      align: 'center' as const,
      width: 160,
      render: function FeeCell(fee: number | null, row: any) {
        const record = row as TalentInviteLink;
        if (record.type === 'ALL_DEALS') {
          return <span>N/A</span>;
        }
        return (
          <span>{fee != null ? `${fee}%` : `${effectiveFallbackFee}%`}</span>
        );
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      align: 'center' as const,
      width: 150,
      render: (date: string) => moment(date).format('MM/DD/YYYY HH:mm'),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      fixed: 'right' as const,
      align: 'center' as const,
      width: 150,
      render: function ActionsWrap(_: unknown, row: any) {
        const record = row as TalentInviteLink;
        return (
          <Space>
            <Tooltip title="Copy link">
              <Button
                icon={<CopyOutlined />}
                size="small"
                onClick={() => handleCopy(record.code)}
              />
            </Tooltip>
            <Popconfirm
              title="Are you sure you want to deactivate this invite link?"
              onConfirm={() => handleDeactivate(record.id)}
              okText="Deactivate"
              cancelText="Cancel"
            >
              <Tooltip title="Deactivate link">
                <Button icon={<StopOutlined />} size="small" danger />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Modal
      title="Talent Invite Links"
      width={1000}
      visible={show}
      onCancel={handleClose}
      footer={null}
    >
      <Space className={styles.actions}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreateCampaignLink}
          loading={creatingCampaignLink}
        >
          Generate Campaign Link
        </Button>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreateAllDealsLink}
          loading={creatingAllDealsLink}
        >
          Generate Global Link
        </Button>
      </Space>
      <Table
        columns={columns}
        data={links}
        loading={campaignLinksLoading || allLinksLoading}
        pagination={false}
      />
    </Modal>
  );
};

export default TalentInviteLinksModal;
